import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/errors";
import { CheckCircle2, XCircle, Loader2, Download, Sparkles } from "lucide-react";

const MAX_POLLS = 12;
const POLL_INTERVAL_MS = 2500;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ phase: "polling", data: null, error: null });
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session_id" });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ phase: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ phase: "error", error: formatApiError(e) });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20" data-testid="checkout-success-page">
      {state.phase === "polling" && (
        <div className="card p-10 text-center" data-testid="checkout-polling">
          <Loader2 size={36} className="mx-auto mb-4 animate-spin" style={{ color: "var(--ai)" }} />
          <div className="font-display font-bold text-2xl mb-2">Confirming your purchase</div>
          <div style={{ color: "var(--text-dim)" }}>Hang tight — this usually takes a few seconds.</div>
        </div>
      )}
      {state.phase === "paid" && (
        <div className="card p-10 text-center" style={{ borderColor: "rgba(34,197,94,0.5)" }} data-testid="checkout-success">
          <CheckCircle2 size={44} className="mx-auto mb-4" style={{ color: "var(--success)" }} />
          <div className="label-mono mb-2" style={{ color: "var(--success)" }}>Payment received</div>
          <div className="font-display font-black text-3xl tracking-tighter mb-2">
            {state.data?.package_name}
          </div>
          <div className="mb-6" style={{ color: "var(--text-dim)" }}>
            ${(state.data?.amount_total / 100).toFixed(2)} {state.data?.currency?.toUpperCase()}
          </div>
          {state.data?.package_type === "pro_subscription" ? (
            <div className="border p-5 mb-6" style={{ borderColor: "rgba(37,99,235,0.4)" }}>
              <Sparkles size={20} className="mx-auto mb-2" style={{ color: "var(--ai)" }} />
              <div className="font-medium">Pro is now active — auto-renews until you cancel.</div>
              <Link to="/profile" className="btn btn-secondary mt-3" data-testid="goto-profile-button">
                Manage subscription
              </Link>
            </div>
          ) : (
            <div className="border p-5 mb-6" style={{ borderColor: "var(--border)" }}>
              <Download size={20} className="mx-auto mb-2" />
              <div className="font-medium mb-2">Your pack is in your profile.</div>
              <Link to="/profile" className="btn btn-secondary mt-2" data-testid="goto-profile-button">Go to profile</Link>
            </div>
          )}
          <Link to="/store" className="btn btn-primary" data-testid="back-to-store">Back to store</Link>
        </div>
      )}
      {state.phase === "expired" && (
        <div className="card p-10 text-center" data-testid="checkout-expired">
          <XCircle size={36} className="mx-auto mb-4" style={{ color: "var(--human)" }} />
          <div className="font-display font-bold text-2xl mb-2">Session expired</div>
          <Link to="/store" className="btn btn-primary mt-4">Try again</Link>
        </div>
      )}
      {state.phase === "timeout" && (
        <div className="card p-10 text-center" data-testid="checkout-timeout">
          <Loader2 size={36} className="mx-auto mb-4" />
          <div className="font-display font-bold text-2xl mb-2">Still processing…</div>
          <div style={{ color: "var(--text-dim)" }}>Your payment is being confirmed. Check your email shortly.</div>
          <Link to="/profile" className="btn btn-primary mt-4">Go to profile</Link>
        </div>
      )}
      {state.phase === "error" && (
        <div className="card p-10 text-center" data-testid="checkout-error">
          <XCircle size={36} className="mx-auto mb-4" style={{ color: "var(--human)" }} />
          <div className="font-display font-bold text-2xl mb-2">Something went wrong</div>
          <div className="tag tag-wrong inline-block">{state.error}</div>
          <div className="mt-6"><Link to="/store" className="btn btn-secondary">Back to store</Link></div>
        </div>
      )}
    </div>
  );
}
